import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Loader2Icon } from 'lucide-react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import axios from 'axios';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { NbButton } from '@/components/Neobrut';
import { API_BASE_URL } from '@/constants';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuthToken } from '@/hooks/useAuthToken';

interface ReportPubProps {
  open: boolean;
  onClose: () => void;
  post: Pet;
}

interface FormData {
  reason: 'T&C-violation' | 'solved_case' | undefined;
}

interface ReportResponse {
  success: boolean;
  message: string;
}

export const ReportPub: FC<ReportPubProps> = ({ open, onClose, post }) => {
  const navigate = useNavigate();
  const { token, isTokenExpired } = useAuthToken();
  const {
    watch,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      reason: undefined,
    },
  });

  const reportMutation = useMutation({
    mutationFn: async (data: FormData) =>
      axios.post<ReportResponse>(
        `${API_BASE_URL}/report-publications/`,
        {
          pet: post.id,
          reason: data.reason,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      ),
  });

  const reason = watch('reason');

  const onSubmit = (data: FormData) => {
    if (!token || isTokenExpired(token)) {
      toast.info('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
      navigate('/login');
      return;
    }
    if (reason === undefined) {
      return;
    }

    reportMutation.mutate(data, {
      onSuccess: () => {
        onClose();
      },
      onError: (error) => {
        console.error('Error reporting publication:', error);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="w-full max-w-[92%] p-5 sm:max-w-md"
        style={{
          background: 'var(--nb-paper)',
          border: '3px solid var(--nb-ink)',
          boxShadow: '6px 6px 0 var(--nb-ink)',
          borderRadius: 'var(--nb-radius)',
        }}
      >
        <DialogHeader>
          <div
            className="text-[10px] font-bold uppercase tracking-[0.08em] opacity-60"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            / reportar
          </div>
          <DialogTitle className="font-display text-[22px] leading-tight">
            ¿Querés reportar esta publicación?
          </DialogTitle>
          <DialogDescription className="text-[13px] font-medium">
            Si considerás que esta publicación no cumple con las políticas o detectaste que es un
            caso ya resuelto, reportala.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="pt-3">
          <div className="flex flex-col gap-1.5">
            <label
              className="text-[11px] font-bold uppercase tracking-[0.08em]"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              Motivo
            </label>
            <Controller
              name="reason"
              control={control}
              render={({ field }) => (
                <Select onValueChange={field.onChange}>
                  <SelectTrigger className="h-10 w-full border-2 border-[var(--nb-ink)] bg-white font-medium">
                    <SelectValue placeholder="Seleccioná un motivo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="T&C-violation" className="hover:cursor-pointer">
                      Violación de Términos y Condiciones
                    </SelectItem>
                    <SelectItem value="solved_case" className="hover:cursor-pointer">
                      Caso resuelto
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.reason && (
              <span
                className="text-[11px] font-semibold"
                style={{ color: '#b42020', fontFamily: '"JetBrains Mono", monospace' }}
              >
                {errors.reason.message}
              </span>
            )}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <NbButton type="button" fill="paper" onClick={onClose}>
              Cancelar
            </NbButton>
            <NbButton
              type="submit"
              fill="urgent"
              disabled={reportMutation.isPending || !reason}
            >
              {reportMutation.isPending && <Loader2Icon className="h-4 w-4 animate-spin" />}
              Reportar
            </NbButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
